import { AppType } from '@watershed/config/constants';
import { getPublicRuntimeConfig } from '@watershed/config/utils/publicRuntimeConfig';
import { BadInputError } from '@watershed/errors/BadInputError';

function ensureSlug(value: string, label: string): string {
  BadInputError.invariant(value, `Docs ${label} slug must be provided`);
  BadInputError.invariant(
    !value.includes('/'),
    `Docs ${label} slug must be a single path segment`
  );
  return value;
}

export const DOCS_ROUTE_PREFIX = '/docs';

export interface DocsRouteOptions {
  /** If set, include /docs prefix regardless of env. Useful for contexts like email rendering where NEXT_PUBLIC_APPLICATION is not set. */
  includeDocsPrefix?: boolean;
}

// app-docs uses Next.js basePath, so internal URLs use no prefix
// but in Dashboard, we need to prefix the routes with /docs
export const routeForCurrentDocsPrefix = (
  options?: DocsRouteOptions
): string => {
  if (options?.includeDocsPrefix) {
    return DOCS_ROUTE_PREFIX;
  }
  const app = getPublicRuntimeConfig('NEXT_PUBLIC_APPLICATION');
  if (app === AppType.Dashboard) {
    return DOCS_ROUTE_PREFIX;
  } else {
    return '';
  }
};

export const routeForDocs = (options?: DocsRouteOptions): string => {
  const prefix = routeForCurrentDocsPrefix(options);
  return prefix === '' ? '/' : prefix;
};

export const routeForDocsGlossary = (options?: DocsRouteOptions): string =>
  `${routeForCurrentDocsPrefix(options)}/glossary`;

export const routeForDocsGlossaryTerm = (
  termSlug: string,
  options?: DocsRouteOptions
): string => {
  return `${routeForDocsGlossary(options)}/${ensureSlug(termSlug, 'glossary term')}`;
};

export const routeForDocsCategory = (
  categorySlug: string,
  options?: DocsRouteOptions
): string => {
  return `${routeForCurrentDocsPrefix(options)}/category/${ensureSlug(categorySlug, 'category')}`;
};

export const routeForDocsPage = (
  pageSlug: string,
  options?: DocsRouteOptions
): string => {
  return `${routeForCurrentDocsPrefix(options)}/${ensureSlug(pageSlug, 'page')}`;
};

export const routeForDocsSearch = (
  query: string,
  options?: DocsRouteOptions
): string => {
  const suffix = query ? '/' + encodeURIComponent(query) : '';
  return `${routeForCurrentDocsPrefix(options)}/search${suffix}`;
};
